// js/theme.js — Dark mode: state, persistence, toggle helpers

// ── State ─────────────────────────────────────────────────────────────────────

export function isDark() {
  return document.body.classList.contains('dark');
}

/**
 * Apply or remove dark mode and persist the choice.
 * Syncs every toggle widget currently in the DOM.
 */
export function setDark(on) {
  document.body.classList.toggle('dark', on);
  localStorage.setItem('tl_dark', String(on));
  _syncHeaderBtn(on);
  _syncSettingsToggle(on);
}

export function toggleDark() {
  setDark(!isDark());
}

// ── Init (called from app.js after body is available) ─────────────────────────

/**
 * The anti-flash inline script in index.html already toggled body.dark before
 * first paint. initTheme() just wires the header button click handler and
 * makes sure the icon reflects the current state.
 */
export function initTheme() {
  const btn = document.getElementById('dark-mode-btn');
  if (!btn) return;
  _syncHeaderBtn(isDark());
  btn.addEventListener('click', toggleDark);
}

// ── Settings toggle widget ─────────────────────────────────────────────────────

/**
 * Build and return a pill toggle <button> for the Settings "Appearance" card.
 * Wires its own click handler; stays in sync via setDark().
 */
export function buildSettingsToggle() {
  const on  = isDark();
  const btn = document.createElement('button');
  btn.id    = 'dm-settings-toggle';
  btn.setAttribute('role', 'switch');
  btn.setAttribute('aria-checked', String(on));
  btn.setAttribute('aria-label', 'Toggle dark mode');
  _applyPillStyle(btn, on);

  const knob = document.createElement('span');
  knob.setAttribute('data-knob', '');
  _applyKnobStyle(knob, on);

  btn.appendChild(knob);
  btn.addEventListener('click', toggleDark);
  return btn;
}

// ── Private sync helpers ──────────────────────────────────────────────────────

function _syncHeaderBtn(on) {
  const btn = document.getElementById('dark-mode-btn');
  if (btn) btn.textContent = on ? '☀️' : '🌙';
}

function _syncSettingsToggle(on) {
  const btn = document.getElementById('dm-settings-toggle');
  if (!btn) return;
  btn.setAttribute('aria-checked', String(on));
  _applyPillStyle(btn, on);
  const knob = btn.querySelector('[data-knob]');
  if (knob) _applyKnobStyle(knob, on);
}

function _applyPillStyle(btn, on) {
  btn.style.cssText = [
    'position:relative',
    'display:inline-flex',
    'align-items:center',
    'width:44px',
    'height:24px',
    'border-radius:12px',
    'border:none',
    'cursor:pointer',
    'flex-shrink:0',
    'transition:background 0.2s',
    `background:${on ? '#49B9FF' : '#CBD5E1'}`,
  ].join(';');
}

function _applyKnobStyle(knob, on) {
  knob.style.cssText = [
    'display:block',
    'width:18px',
    'height:18px',
    'border-radius:50%',
    'background:#fff',
    'position:absolute',
    'box-shadow:0 1px 4px rgba(0,0,0,0.25)',
    'transition:transform 0.2s',
    `transform:translateX(${on ? '23px' : '3px'})`,
  ].join(';');
}
